/**
 * EstacionesPage — CRUD simple de estaciones de servicio (admin only).
 * Los pilotos y operaciones solo las ven en el dropdown del formulario de carga.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Button, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert, CircularProgress, Typography,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StoreIcon from '@mui/icons-material/Store';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
function getEmpresaId() {
  // Superadmin puede cambiar de empresa desde el sidebar (Layout escribe
  // `empresa_activa` en localStorage). Respetar ese valor por encima de
  // `user.company_id` para no quedar fuera de sync con el selector activo.
  const activa = localStorage.getItem('empresa_activa');
  if (activa) return activa;
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

export default function EstacionesPage() {
  const empresaId = getEmpresaId();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editando: null });
  const [form, setForm] = useState({ nombre: '', direccion: '', contacto: '', telefono: '', notas: '' });
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/combustible/estaciones?empresa_id=${empresaId}&solo_activas=false`,
        { headers: getAuthHeaders() }
      );
      if (r.ok) setRows(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNueva = () => {
    setForm({ nombre: '', direccion: '', contacto: '', telefono: '', notas: '' });
    setDialog({ open: true, editando: null });
  };

  const abrirEditar = (e) => {
    setForm({
      nombre: e.nombre || '', direccion: e.direccion || '',
      contacto: e.contacto || '', telefono: e.telefono || '', notas: e.notas || '',
    });
    setDialog({ open: true, editando: e });
  };

  const guardar = async () => {
    if (!form.nombre.trim()) return;
    try {
      const url = dialog.editando
        ? `${API_BASE}/turismo/combustible/estaciones/${dialog.editando.id}?empresa_id=${empresaId}`
        : `${API_BASE}/turismo/combustible/estaciones`;
      const method = dialog.editando ? 'PATCH' : 'POST';
      const body = dialog.editando ? form : { empresa_id: empresaId, ...form };
      const r = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(body) });
      if (r.ok) {
        setSnack({ open: true, msg: dialog.editando ? 'Estación actualizada' : 'Estación creada', severity: 'success' });
        setDialog({ open: false, editando: null });
        cargar();
      } else {
        setSnack({ open: true, msg: 'Error al guardar', severity: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, msg: 'Error de red', severity: 'error' });
    }
  };

  const desactivar = async (id) => {
    if (!window.confirm('¿Desactivar esta estación? No se borra, solo deja de aparecer en los dropdowns.')) return;
    try {
      const r = await fetch(
        `${API_BASE}/turismo/combustible/estaciones/${id}?empresa_id=${empresaId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      if (r.ok) {
        setSnack({ open: true, msg: 'Estación desactivada', severity: 'success' });
        cargar();
      }
    } catch (e) {
      setSnack({ open: true, msg: 'Error de red', severity: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Estaciones de servicio disponibles para elegir al registrar una carga.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddCircleIcon />} onClick={abrirNueva}
          sx={{
            textTransform: 'none', fontWeight: 700,
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
          }}
        >
          Nueva estación
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Dirección</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Contacto</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Teléfono</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Estado</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#7c3aed' }} />
              </TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                Sin estaciones registradas. Crea la primera con "Nueva estación".
              </TableCell></TableRow>
            )}
            {!loading && rows.map(e => (
              <TableRow key={e.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <StoreIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                    {e.nombre}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{e.direccion || '—'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{e.contacto || '—'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{e.telefono || '—'}</TableCell>
                <TableCell align="center">
                  <Chip label={e.activo ? 'Activa' : 'Inactiva'} size="small"
                    sx={{
                      bgcolor: e.activo ? '#dcfce7' : '#fee2e2',
                      color: e.activo ? '#166534' : '#991b1b',
                      fontWeight: 700, fontSize: 10.5,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => abrirEditar(e)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {e.activo && (
                    <IconButton size="small" onClick={() => desactivar(e.id)}>
                      <DeleteIcon fontSize="small" sx={{ color: '#dc2626' }} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, editando: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialog.editando ? 'Editar estación' : 'Nueva estación'}
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" required autoFocus label="Nombre *"
            value={form.nombre} onChange={(e) => setForm(p => ({ ...p, nombre: e.target.value }))}
            sx={{ mt: 1, mb: 1.5 }}
          />
          <TextField fullWidth size="small" label="Dirección"
            value={form.direccion} onChange={(e) => setForm(p => ({ ...p, direccion: e.target.value }))}
            sx={{ mb: 1.5 }}
          />
          <TextField fullWidth size="small" label="Contacto (nombre)"
            value={form.contacto} onChange={(e) => setForm(p => ({ ...p, contacto: e.target.value }))}
            sx={{ mb: 1.5 }}
          />
          <TextField fullWidth size="small" label="Teléfono"
            value={form.telefono} onChange={(e) => setForm(p => ({ ...p, telefono: e.target.value }))}
            sx={{ mb: 1.5 }}
          />
          <TextField fullWidth size="small" multiline rows={2} label="Notas"
            value={form.notas} onChange={(e) => setForm(p => ({ ...p, notas: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, editando: null })} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={!form.nombre.trim()}
            sx={{
              textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
